<template>
  <div class="faq-container">
    <h2 v-if="title !== ''" class="title">
      {{ title || `${capitalizedDescriptor} FAQs` }}
    </h2>
    <ul class="faq-list">
      <li v-for="(item, index) in faqs" :key="index" class="faq-item">
        <div
          class="faq-header"
          tabindex="0"
          role="button"
          :aria-expanded="openedIndex === index"
          @click="toggleFaq(index)"
          @keydown.enter="toggleFaq(index)"
          @keydown.space="toggleFaq(index)"
        >
          <h3>
            {{ item.question }}
          </h3>
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            aria-hidden="true"
            :class="{ 'payment-logo-open': openedIndex === index }"
            class="payment-logo"
          >
            <rect
              width="32"
              height="32"
              rx="16"
              fill="var(--color-primary)"
            ></rect>
            <path
              d="M11.41 12.58L16 17.17L20.59 12.58L22 14L16 20L10 14L11.41 12.58Z"
              fill="var(--color-background-primary)"
            ></path>
          </svg>
        </div>
        <div class="faq-answer" :class="{ 'faq-answer-open': openedIndex === index }">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <p v-html="item.answer"></p>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { useCapitalizedDescriptor } from '@/composables/useCapitalizedDescriptor'
import TrackingService from '@/services/TrackingService'
import { ActionType, EventSubtypes, SourceObjectKey, SourceObjectValueElementSubtype } from '@/types/events'

const capitalizedDescriptor = useCapitalizedDescriptor()

const props = defineProps<{
  title?: string
  items?: readonly { question: string, answer: string }[]
}>()

const defaultFaqs = [
  {
    question: 'I don’t recognize a charge — what should I do?',
    answer: `If you’ve noticed a charge that you don’t recognize, please contact us, so we can look
    into it for you. To help us identify the transaction and respond to your request, we’ll need the following details:
    <ul>
    <li>The first 6 digits (BIN) and the last 4 digits of the card used for the transaction</li>
    <li>Your email address, to update you on your request</li>
    </ul>
    Once we receive this information, we’ll investigate the charge and get back to you as soon as possible.`,
    open: false,
  },
  {
    question: 'What is BIN, and is it safe to share it?',
    answer: `BIN stands for Bank Identification Number, and it is the first six digits of your card number.
    Sharing your BIN is generally safe, as it is used to identify the bank that issued your card.
    However, you should always be cautious about sharing any sensitive information online.`,
    open: false,
  },
  {
    question: 'What\'s the name of the site that charged me?',
    answer:
      `Once you provide us with the details of the transaction you’re asking about, we will investigate it
      and get back to you with information about the platform where the charge occurred.`,
    open: false,
  },
  {
    question: 'Can I get a refund?',
    answer: `As outlined in our Payment and Refund Policy, you may be eligible for a refund under
    specific legal conditions. To request a refund, please select “Request a refund” under the
    “Select your issue” section in the upper left corner of this page, or visit the site where the
    charge occurred and contact support using any of the available methods.
We typically process refund requests quickly, so it's faster and easier than contacting your bank.`,
    open: false,
  },
]

const faqs = computed(() => props.items ?? defaultFaqs)
const openedIndex = ref<number | null>(null)

const toggleFaq = (index: number): void => {
  if (openedIndex.value === index) {
    openedIndex.value = null
  } else {
    openedIndex.value = index
    TrackingService.trackComponentInteraction(
      ActionType.ClickShow,
      [
        { key: SourceObjectKey.ElementSubtype, value: SourceObjectValueElementSubtype.Faq },
        { key: SourceObjectKey.FaqSubtype, value: `${faqs.value[index]?.question}` },
      ],
      EventSubtypes.DescriptorPage,
    )
  }
}

onMounted((): void => {
  TrackingService.trackComponentInteraction(
    ActionType.Show,
    [
      { key: SourceObjectKey.ElementSubtype, value: SourceObjectValueElementSubtype.Faq },
    ],
    EventSubtypes.DescriptorPage,
  )
})
</script>

<style scoped lang="scss">
.title {
  margin-bottom: 24px;
  text-align: center;
  color: var(--color-text);
  font-size: 24px;
  font-weight: var(--font-weight-bold);
  line-height: 36px;
}

.faq-container {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.faq-item{
  display: flex;
  flex-direction: column;
  padding: 16px;
  border: 1px solid #ebebeb;
  border-radius: 12px;
}

.faq-list{
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  gap: 8px;
}

.faq-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.faq-answer {
  overflow: hidden;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease-in-out 0.1s,
    padding 0.3s ease-in-out;
  opacity: 0;
}

.faq-answer-open {
  max-height: 300px;
  padding-top: 16px;
  padding-bottom: 0;
  opacity: 1;
}

.payment-logo{
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.payment-logo-open {
  transform: rotate(180deg);
}

p {
  margin: 0;
  color: #000;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}
</style>
